import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Button } from '../../../shared/components/ui/Button';
import { Home, Target, ClipboardList, Settings, FileText, ClipboardCheck, CheckSquare, GraduationCap, BarChart3, Scale } from 'lucide-react';
import { useOrganizationSettings } from '../hooks/useOrganizationSettings';
import { useOrganizationDevelopmentAccess } from '../hooks/useOrganizationDevelopmentAccess';
import { useCalibrationAccess } from '../hooks/useCalibrationAccess';
import { useAuth } from '../../../contexts/AuthContext';
import { useEmployees } from '../hooks/useSettings';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  requiresAccess?: boolean;
  requiresCalibrationAccess?: boolean;
}

const baseNavItems: NavItem[] = [
  { path: '/strategic-performance/approvals', label: 'Approvals', icon: <CheckSquare className="h-4 w-4" /> },
  { path: '/strategic-performance/employee-scorecards', label: 'My Scorecards', icon: <FileText className="h-4 w-4" /> },
  { path: '/strategic-performance/my-assessment', label: 'My Assessment', icon: <ClipboardCheck className="h-4 w-4" /> },
  { path: '/strategic-performance/calibration', label: 'Performance Calibration', icon: <Scale className="h-4 w-4" />, requiresCalibrationAccess: true },
  { path: '/strategic-performance/organization-performance', label: 'Organization Performance', icon: <BarChart3 className="h-4 w-4" /> },
  { path: '/strategic-performance/development-plans', label: 'Development Plans', icon: <GraduationCap className="h-4 w-4" />, requiresAccess: true },
  { path: '/strategic-performance/reports', label: 'Reports', icon: <BarChart3 className="h-4 w-4" /> },
  { path: '/strategic-performance/scorecards', label: 'Annual Corporate Scorecards', icon: <ClipboardList className="h-4 w-4" /> },
  { path: '/strategic-performance/strategic-plans', label: 'Strategic Plans', icon: <Target className="h-4 w-4" /> },
  { path: '/strategic-performance/settings', label: 'Settings', icon: <Settings className="h-4 w-4" /> },
];

const Navigation: React.FC = () => {
  const location = useLocation();
  const { logoUrl, isLoading, refetch } = useOrganizationSettings();
  const { user } = useAuth();
  const { hasOrgDevelopmentAccess, isLoading: loadingAccess } = useOrganizationDevelopmentAccess();
  const { hasCalibrationAccess, isLoading: loadingCalibrationAccess } = useCalibrationAccess();
  const { data: employees = [] } = useEmployees();
  
  const isAdmin = user?.isStrategicPerformanceSuperUser || false;
  const canAccessDevPlans = isAdmin || hasOrgDevelopmentAccess;
  
  // Find current user's employee record to check role
  const currentEmployee = employees.find(emp => emp.userId === user?.id);
  const userRole = currentEmployee?.role as 'CEO' | 'DepartmentHead' | 'Manager' | 'Supervisor' | 'Employee' | undefined;
  const isCEO = userRole === 'CEO';
  const isDeptHead = userRole === 'DepartmentHead';
  const isManager = userRole === 'Manager';
  const isSupervisor = userRole === 'Supervisor';
  const isEmployee = userRole === 'Employee';

  // Filter nav items based on role permissions (per access matrix)
  const navItems = baseNavItems.filter(item => {
    const path = item.path;

    // Settings: Admin only (Edit) - no access for CEO, Dept Head, Manager/Supervisor, Employee
    if (path === '/strategic-performance/settings') {
      return isAdmin;
    }

    // CEO: Approvals, Strategic Plans, Corporate Scorecards, Calibration (if committee), Org Performance, Reports. No Settings, No My Scorecards, No My Assessments.
    if (isCEO) {
      const ceoAllowedPaths = [
        '/strategic-performance/approvals',
        '/strategic-performance/strategic-plans',
        '/strategic-performance/scorecards',
        '/strategic-performance/calibration',
        '/strategic-performance/organization-performance',
        '/strategic-performance/reports',
      ];
      if (ceoAllowedPaths.includes(path)) {
        // Calibration: CEO only if committee member
        if (path === '/strategic-performance/calibration') {
          return !loadingCalibrationAccess && hasCalibrationAccess;
        }
        return true;
      }
      return false;
    }

    // Strategic Plans: Admin/CEO (Edit), Dept Head/Manager/Supervisor/Employee (Read) - show to all
    // Annual Corporate Scorecards: same - show to all
    // (Edit vs Read enforced on the page)

    // Approvals: Admin, CEO, Dept Head, Manager/Supervisor (Full) - hide for Employee
    if (path === '/strategic-performance/approvals') {
      return !isEmployee;
    }

    // My Scorecards: Admin, Dept Head, Manager/Supervisor, Employee (Full) - hide for CEO
    if (path === '/strategic-performance/employee-scorecards') {
      return !isCEO;
    }

    // My Assessments: same - hide for CEO
    if (path === '/strategic-performance/my-assessment') {
      return !isCEO;
    }

    // Calibration: all roles only if committee member
    if (path === '/strategic-performance/calibration') {
      return !loadingCalibrationAccess && hasCalibrationAccess;
    }

    // Organization Performance: Admin, CEO only - no access for Dept Head, Manager/Supervisor, Employee
    if (path === '/strategic-performance/organization-performance') {
      return isAdmin || isCEO;
    }

    // Development Plans: all roles only if in OD Team
    if (item.requiresAccess && !loadingAccess && !canAccessDevPlans) {
      return false;
    }

    // Reports: Admin, CEO, Dept Head (Full) - no access for Manager/Supervisor, Employee
    if (path === '/strategic-performance/reports') {
      return !isManager && !isSupervisor && !isEmployee;
    }

    return true;
  });

  // Listen for logo updates
  React.useEffect(() => {
    const handleLogoUpdate = () => {
      refetch();
    };
    window.addEventListener('logoUpdated', handleLogoUpdate);
    return () => {
      window.removeEventListener('logoUpdated', handleLogoUpdate);
    };
  }, [refetch]);

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <div className="border-b bg-white px-3 sm:px-6 py-4">
      <div className="flex items-center gap-3 sm:gap-6 min-w-0">
        {/* Logo */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {!isLoading && logoUrl && (
            <Link to="/strategic-performance/approvals" className="flex items-center min-w-0">
              <img
                src={logoUrl}
                alt="Organization Logo"
                className="h-10 sm:h-12 w-auto max-w-[180px] sm:max-w-[300px] object-contain"
              />
            </Link>
          )}
        </div>
        <nav className="flex gap-1 overflow-x-auto flex-1 min-w-0 pb-1 -mb-1" style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}>
          <div className="flex gap-1 flex-nowrap pr-2">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} className="flex-shrink-0">
                <Button 
                  variant={isActive(item.path) ? 'default' : 'ghost'}
                  size="sm"
                  className="gap-1.5 sm:gap-2 whitespace-nowrap text-xs sm:text-sm"
                >
                  {item.icon}
                  {item.label}
                </Button>
              </Link>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
};

export default Navigation;
